import { CommonSurgery, Location, Surgery } from '~/storm'
import { isDayOvernightKey } from '~/utils/dayOvernight'
import { getLevelOfCareNprName, getMainSurgeon, isUnScheduledSurgery } from '~/utils/dips'
import { isNullish } from '~/utils/guards'
import { getSurgeryMetadata } from '~/utils/surgeryMetadata'

import {
    getIsWaiting,
    getPatientAvailabilityFilterValues,
    isPatientAvailableOnEntireDateRange,
} from '../WaitingList/Drawer/PatientDrawerTab/StatusesAndRemarks/PatientAvailability/utils'
import { getSuggestionVisibility } from '../WaitingList/shared/PlanningNote/shared/suggestionVisibility'
import {
    externalDocumentNeedsFollowUp,
    hasActiveInfectionCondition,
    letterNotSent,
    selfDeclarationFormNeedsFollowUp,
    testResultsNeedsFollowUp,
} from '../WaitingList/shared/utils'

export function confirmed(confirmedValues: string[]) {
    return (item: Surgery) => {
        const isPatientConfirmed = Boolean(getSurgeryMetadata(item)?.patient_confirmed)

        return (
            confirmedValues.length === 0 ||
            (confirmedValues.includes('Ikke bekreftet') && !isPatientConfirmed) ||
            (confirmedValues.includes('Bekreftet') && isPatientConfirmed)
        )
    }
}

export function dayOvernight(dayOvernightValues: string[]) {
    return (item: Surgery) => {
        if (dayOvernightValues.length === 0) return true

        const levelOfCareNprName = getLevelOfCareNprName(item)

        // If valid value, then filter, otherwise include to not hide invalid values
        return isDayOvernightKey(levelOfCareNprName) ? dayOvernightValues.includes(levelOfCareNprName) : true
    }
}

export function mainSurgeon<T extends Surgery>(practitionerValues: string[]) {
    return (item: T) => {
        // Include item if no practitioners are specified
        if (!practitionerValues.length) return true

        // Include item only if the main surgeon is in the list
        const mainSurgeonShortName = getMainSurgeon(item.surgeryResources)?.shortName
        return mainSurgeonShortName ? practitionerValues.includes(mainSurgeonShortName) : false
    }
}

export function cleared(clearedValues: string[]) {
    return (item: CommonSurgery) => {
        const isPatientCleared = getSurgeryMetadata(item)?.patient_ready
        const isUnscheduledSurgery = isUnScheduledSurgery(item)

        return (
            clearedValues.length === 0 ||
            (clearedValues.includes('new') && (isUnscheduledSurgery ? isNullish(isPatientCleared) : false)) ||
            (clearedValues.includes('notCleared') && (isUnscheduledSurgery ? isPatientCleared === false : !isPatientCleared)) ||
            (clearedValues.includes('cleared') && isPatientCleared === true)
        )
    }
}

export function patientAvailable(
    patientAvailabilityFromFilter?: { label: string; value: string },
    patientAvailabilityToFilter?: { label: string; value: string }
) {
    return (item: Surgery) => {
        const { startDate, endDate, isDateError } = getPatientAvailabilityFilterValues(patientAvailabilityFromFilter, patientAvailabilityToFilter)

        const noFilterValues = patientAvailabilityFromFilter === undefined && patientAvailabilityToFilter === undefined

        return noFilterValues || isDateError || isPatientAvailableOnEntireDateRange(startDate, endDate, item)
    }
}

export function patientIsWaiting(waitingValues: string[]) {
    return (item: Surgery) => waitingValues.length === 0 || (waitingValues.includes('waiting') && getIsWaiting(item))
}

export function followUp(followUpValues: string[]) {
    return (item: Surgery) => {
        if (followUpValues.length === 0) return true

        return followUpValues.some(value => {
            switch (value) {
                case 'selfDeclarationForm':
                    return selfDeclarationFormNeedsFollowUp(item)
                case 'externalDocument':
                    return externalDocumentNeedsFollowUp(item)
                case 'testResults':
                    return testResultsNeedsFollowUp(item)
                case 'Brev ikke sendt':
                    return letterNotSent(item)
                default:
                    return false
            }
        })
    }
}

export function hasInfection(infectionValues: string[]) {
    return (item: Surgery) => {
        if (infectionValues.length === 0) return true

        return infectionValues.includes('Smitte') && hasActiveInfectionCondition(item)
    }
}

export function isCancerSurgery(cancerValues: string[]) {
    return (item: Surgery) => {
        if (cancerValues.length === 0) return true

        const surgeryMetadata = getSurgeryMetadata(item)
        const isCancerSurgery = surgeryMetadata?.surgery_cancer === true

        return cancerValues.includes('surgery-cancer') && isCancerSurgery
    }
}

export function metadataSuggestionNeedsReview(metadataSuggestionValues: string[]) {
    return (item: Surgery) => {
        if (metadataSuggestionValues.length === 0) return true
        if (!metadataSuggestionValues.includes('metadata-surgery-suggestion')) return true

        return getSuggestionVisibility({ surgery: item, commentText: item.planningNote }).needsReview
    }
}

function normalizeCommentText(text: string | null | undefined): string {
    return (text ?? '').replace(/\s+/g, ' ').trim().toLocaleLowerCase()
}

export function hasPlanningNote(hasPlanningNoteValues: string[]) {
    return (item: Surgery) => {
        if (hasPlanningNoteValues.length === 0) return true
        if (!hasPlanningNoteValues.includes('has-planning-note')) return true

        return normalizeCommentText(item.planningNote).length > 0
    }
}

export function planningNoteEmpty(values: string[]) {
    return (item: Surgery) => {
        if (values.length === 0) return true
        if (!values.includes('planning-note-empty')) return true

        return normalizeCommentText(item.planningNote).length === 0
    }
}

export function hasCoordinationComment(values: string[]) {
    return (item: Surgery) => {
        if (values.length === 0) return true
        if (!values.includes('has-coordination-comment')) return true

        return normalizeCommentText(item.contact?.coordinationComment).length > 0
    }
}

export function planningNoteSameAsCoordinationComment(values: string[]) {
    return (item: Surgery) => {
        if (values.length === 0) return true
        if (!values.includes('planning-note-same-as-coordination-comment')) return true

        const planningNote = normalizeCommentText(item.planningNote)
        const coordinationComment = normalizeCommentText(item.contact?.coordinationComment)

        return planningNote === coordinationComment
    }
}

export function planningNoteDifferentFromCoordinationComment(values: string[]) {
    return (item: Surgery) => {
        if (values.length === 0) return true
        if (!values.includes('planning-note-different-from-coordination-comment')) return true

        const planningNote = normalizeCommentText(item.planningNote)
        const coordinationComment = normalizeCommentText(item.contact?.coordinationComment)

        return planningNote !== coordinationComment
    }
}

export function surgeryTypeSameAsPlanningNote(values: string[]) {
    return (item: Surgery) => {
        if (values.length === 0) return true
        if (!values.includes('surgery-type-same-as-planning-note')) return true

        const surgeryType = normalizeCommentText(item.surgeryType?.name)
        const planningNote = normalizeCommentText(item.planningNote)

        return surgeryType === planningNote
    }
}

export function surgeryTypeIncludedInPlanningNote(values: string[]) {
    return (item: Surgery) => {
        if (values.length === 0) return true
        if (!values.includes('surgery-type-included-in-planning-note')) return true

        const surgeryType = normalizeCommentText(item.surgeryType?.name)
        const planningNote = normalizeCommentText(item.planningNote)

        if (!surgeryType || !planningNote) return false

        return new RegExp(`(?<![\\w])${surgeryType.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\w])`, 'i').test(planningNote)
    }
}

export function surgeryTypeDifferentFromPlanningNote(values: string[]) {
    return (item: Surgery) => {
        if (values.length === 0) return true
        if (!values.includes('surgery-type-different-from-planning-note')) return true

        const surgeryType = normalizeCommentText(item.surgeryType?.name)
        const planningNote = normalizeCommentText(item.planningNote)

        return surgeryType !== planningNote
    }
}

export function planningNoteWithoutMergeSeparator(values: string[]) {
    return (item: Surgery) => {
        if (values.length === 0) return true
        if (!values.includes('planning-note-without-merge-separator')) return true

        return !(item.planningNote ?? '').includes('****')
    }
}

function mergeSeparatorCountInPlanningNote(text: string | null | undefined): number {
    return (text?.match(/\*\*\*\*/g) ?? []).length
}

export function planningNoteWithMultipleMergeSeparators(values: string[]) {
    return (item: Surgery) => {
        if (values.length === 0) return true
        if (!values.includes('planning-note-multiple-merge-separators')) return true

        return mergeSeparatorCountInPlanningNote(item.planningNote) >= 2
    }
}
